import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { TextLink } from '../../common/components/typography';
import { CREATE_PARAM, isCreateParam } from '../../common/queryParams/create';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../common/useHrefWithQueryParams';
import { usePanelParams } from '../usePanelParams';
import { useSystemClassifiers } from '../../configuration/useSystemClassifiers';
import { SystemClassifierItemDto } from '../../systemClassifier';
import { DataObjectValidFormDataType, DataSourceFormDataType } from '../schema';
import { SourceType } from '../types';
import {
  CloneFormTableRowButton,
  CreateTableRowButton,
  DeleteFormTableRowButton,
  FormField,
  TanstackFormTable,
  getFormTableFieldName,
} from '../../common/form';
import { useFormContext, useWatch } from 'react-hook-form';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../translations/DataSourcesFormTable.translations.json';
import { createDefaultDataSource, DataSourceDrawer } from './DataSourceDrawer';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { FormTablePreviewOnlyField } from '../../common/form/table/tanstack/FormTablePreviewOnlyField';
import { useSystemClassifierItems } from '../../configuration';

const { addTranslations, useTranslation } = i18nNsFactory(
  'data-object-source-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<DataSourceFormDataType>();

interface DataSourcesFormTableProps {
  defaultSourceType?: SourceType | null;
}

export const DataSourcesFormTable: FC<DataSourcesFormTableProps> = ({
  defaultSourceType,
}) => {
  const { t } = useTranslation();
  const [sourceType, setSourceType] = useState(defaultSourceType);
  const { systemClassifierItems: sourceTypes } =
    useSystemClassifiers('DATA_SOURCE_TYPE').DATA_SOURCE_TYPE;
  const { getValues, setValue } = useFormContext<DataObjectValidFormDataType>();
  const [dataSources, sourcesCount = 0, groupAndSubgroupId]: [
    DataSourceFormDataType[],
    number | undefined,
    string | undefined,
  ] = useWatch({
    name: ['dataSources', 'dataSources.length', 'groupAndSubgroupId'],
  });
  const [{ dataSourceId, dataSourceIndex }, setPanelParams] = usePanelParams();

  const addRow = useCallback(
    () => setPanelParams({ dataSourceId: CREATE_PARAM }),
    [setPanelParams],
  );

  const editSource = useCallback(
    (source: DataSourceFormDataType, index?: number) => {
      const opts = { shouldDirty: true };
      if (source.groupSource) {
        // If group based source, retain only one
        setValue('dataSources', [source], opts);
      } else {
        if (typeof index === 'number') {
          setValue(`dataSources.${index}`, source, opts);
        } else {
          setValue(
            `dataSources`,
            getValues('dataSources').concat(source),
            opts,
          );
        }
      }

      setSourceType(source.sourceType);
      setPanelParams();
    },
    [getValues, setValue, setPanelParams],
  );

  useEffect(() => {
    if (!sourcesCount) {
      setSourceType(undefined);
    }
  }, [sourcesCount]);

  const onlyOneSource = useMemo(
    () =>
      sourceType !== 'DATABASE' || dataSources.some((item) => item.groupSource),
    [sourceType, dataSources],
  );
  const type = useMemo(
    () => dataSources?.[0]?.sourceType ?? 'FILE',
    [dataSources],
  );

  const methodNames = useSystemClassifierItems('QUERY_METHOD_NAME');

  const columns = useMemo(
    () => [
      columnHelper.accessor('sourceType', {
        header: t('field.sourceType.label'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { width: 150 },
        cell: (props) => (
          <SourceTypeCell {...props} sourceTypes={sourceTypes} />
        ),
      }),
      ...(sourceType === 'DATABASE'
        ? [
            columnHelper.accessor('code', {
              header: t('field.code.label'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: { width: 100 },
              cell: FormTablePreviewOnlyField,
            }),
            columnHelper.accessor('name', {
              header: t('field.name.label'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: {
                width: '30%',
                minWidth: 150,
              },
              cell: FormTablePreviewOnlyField,
            }),
          ]
        : []),
      columnHelper.accessor('dataLocation', {
        header: t(`sourceType.${type}`),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '30%',
          minWidth: 150,
        },
        cell: FormTablePreviewOnlyField,
      }),
      ...(sourceType === 'URL'
        ? [
            columnHelper.accessor('methodNameId', {
              header: t('field.methodNameId.label'),
              enableColumnFilter: false,
              enableSorting: false,
              meta: { width: 100 },
              cell: (ctx) => (
                <FormField
                  size="small"
                  name={getFormTableFieldName(ctx)}
                  preview={{
                    previewOnly: true,
                    render: (value) =>
                      methodNames.find((methodName) => methodName.id === value)
                        ?.name,
                  }}
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor('connectionId', {
        header: t('field.connectionId.label'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '30%',
          minWidth: 150,
        },
      }),
      columnHelper.accessor('description', {
        header: t('field.description.label'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '40%',
          minWidth: 150,
        },
        cell: FormTablePreviewOnlyField,
      }),
      columnHelper.accessor('groupSource', {
        header: t('field.groupSource.label'),
        enableColumnFilter: false,
        enableSorting: false,
        meta: { width: 100 },
        cell: GroupSourceCell,
      }),
      columnHelper.accessor('operations' as any, {
        header: () => (
          <Flex justify="center" gap="small">
            {!(sourceType && onlyOneSource) && (
              <CreateTableRowButton
                title={t('button.add-source')}
                onClick={addRow}
                defaultRowValues={createDefaultDataSource()}
                autoFocusField="sourceType"
              />
            )}
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        cell: (props) => (
          <OperationsCell {...props} onlyOneSource={onlyOneSource} />
        ),
        meta: {
          width: 80,
          className: 'sticky-right',
        },
      }),
    ],
    [addRow, onlyOneSource, sourceType, sourceTypes, t, type],
  );

  const [rowIndex, editableDataSource] = useMemo(() => {
    if (isCreateParam(dataSourceId)) {
      return [undefined, createDefaultDataSource(sourceType)];
    }
    const dataSources = getValues('dataSources');
    if (dataSourceId) {
      return (
        Array.from(dataSources.entries()).find(
          ([, { id }]) => id === Number(dataSourceId),
        ) || []
      );
    }
    const rowIndex = Number(dataSourceIndex);
    return isNaN(rowIndex)
      ? [undefined, undefined]
      : [rowIndex, dataSources[rowIndex]];
  }, [getValues, dataSourceId, dataSourceIndex, sourceType]);

  return (
    <>
      <TanstackFormTable
        name="dataSources"
        columns={columns}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
      {editableDataSource && (
        <DataSourceDrawer
          closeDrawer={setPanelParams}
          index={rowIndex}
          isGrouped={!!groupAndSubgroupId}
          setSource={editSource}
          source={editableDataSource}
          sourcesCount={sourcesCount}
        />
      )}
    </>
  );
};

const SourceTypeCell: FC<
  CellContext<DataSourceFormDataType, any> & {
    sourceTypes: SystemClassifierItemDto[];
  }
> = memo((props) => {
  const [, setPanelParams] = usePanelParams();
  const dataSourceIndex = props.row.index;
  const dataSourceId = props.row.original.id;
  const params = dataSourceId ? { dataSourceId } : { dataSourceIndex };
  const href = useHrefWithQueryParams(params);
  return (
    <FormField
      name={getFormTableFieldName(props)}
      size="small"
      preview={{
        previewOnly: true,
        render: (value) => (
          <TextLink
            id={`dataSources-${props.row.index}-sourceType`}
            href={href}
            onClick={suppressHref(() => setPanelParams(params))}
          >
            {props.sourceTypes.find((st) => st.code === value)?.name}
          </TextLink>
        ),
      }}
    />
  );
});

const GroupSourceCell: FC<CellContext<DataSourceFormDataType, any>> = memo(
  (props) => {
    const name = getFormTableFieldName(props);
    const { t } = useTranslation();
    return (
      <FormField
        name={name}
        size="small"
        preview={{
          previewOnly: true,
          render: (value) =>
            t(value ? 'field.groupSource.yes' : 'field.groupSource.no'),
        }}
      />
    );
  },
);

const OperationsCell: FC<
  CellContext<DataSourceFormDataType, any> & {
    onlyOneSource: boolean;
  }
> = memo((props) => (
  <Flex justify="center" gap="small">
    <CloneFormTableRowButton
      context={props}
      resetId
      disabled={props.onlyOneSource}
      autoFocusField="code"
    />
    <DeleteFormTableRowButton context={props} />
  </Flex>
));
